/** @jsxImportSource @emotion/react */
import Link from 'next/link'
import { useRouter } from 'next/router'
import { FormNextLink } from 'grommet-icons'
import { useTranslation } from 'next-i18next'
import { useFilter, useSelect } from 'react-supabase'
import { ChangeEvent, useCallback, useState } from 'react'
import { Box, Button, Heading, Spinner, Text, TextInput } from 'grommet'

import { routes } from 'routes'
import { ExistingGroup } from 'db-tables'
import DisplayDate from 'components/DisplayDate'
import { shortCodeLength } from 'util/generateShortCode'

export default function JoinGroupForm() {
  const { t } = useTranslation()

  const [value, setValue] = useState('')
  const [shortCode, setShortCode] = useState('')

  const filter = useFilter<ExistingGroup>((query) => query.eq('short_code', shortCode), [shortCode])
  const [{ data: group, fetching }] = useSelect<ExistingGroup>('groups', {
    columns: '*',
    filter,
  })

  const checkGroup = useCallback(() => {
    if (value.length >= shortCodeLength) {
      setShortCode(value)
    }
  }, [value])

  const onChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value
    setValue(nextValue)
    if (nextValue.length >= shortCodeLength) {
      setShortCode(nextValue)
    }
  }, [])

  return (
    <Box direction="column">
      <TextInput
        reverse
        type="text"
        maxLength={15}
        css={{
          border: 'none',
          backgroundColor: 'white',
          boxShadow: '0 5px 10px 0 rgb(0 0 0 / 15%)',
          transition: 'box-shadow .3s',
          '&:hover': {
            boxShadow: '0 10px 20px 0 rgb(0 0 0 / 15%)',
          },
        }}
        size="medium"
        id="short-code"
        name="short-code"
        textAlign="center"
        value={value}
        onChange={onChange}
        placeholder={
          <Text size="small" textAlign="center">
            {t('pages.group.join.enter-group-code')}
          </Text>
        }
        icon={
          fetching ? (
            <Spinner />
          ) : (
            <FormNextLink
              onClick={checkGroup}
              color="accent-2"
              size="32px"
              pointerEvents="auto"
              cursor="pointer"
            />
          )
        }
      />
      {group && group[0] && <GroupJoinInfo group={group[0]} />}
    </Box>
  )
}
const GroupJoinInfo = ({ group }: { group: ExistingGroup }) => {
  const { t } = useTranslation()
  const router = useRouter()
  return (
    <Box
      round="large"
      margin={{ top: 'large' }}
      pad={{ top: 'medium', horizontal: 'large' }}
      align="center"
      elevation="small"
      justify="between"
      direction="column"
      background={{ color: 'white', opacity: 'strong' }}>
      <Heading size="small" margin="small" css={{ display: 'flex', alignItems: 'center' }}>
        {group.name}
      </Heading>
      {group.description && (
        <Text size="medium" alignSelf="center">
          {group.description}
        </Text>
      )}
      {group.created_at && (
        <DisplayDate date={group.created_at} size="small" alignSelf="center" color="gray" />
      )}
      <Box margin={{ vertical: 'medium' }}>
        <Link href={routes.group.view(group.short_code, router.query)} passHref>
          <Button label={t('cta.join')} primary />
        </Link>
      </Box>
    </Box>
  )
}
